import { useState } from 'react';
import Modal from './Modal';
import { PlusIcon, DeleteIcon, ChevronRightIcon } from './Icons';
import { TYPE_CONFIG, HIERARCHY_RULES, CONTAINER_TYPES } from '../constants';
import api from '../api';

function generateTempId() {
  return crypto.randomUUID();
}

function TemplateTreeNode({ item, allItems, depth, onUpdate, onDelete, onAddChild }) {
  const [expanded, setExpanded] = useState(true);
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
  const Icon = config.icon;
  const children = allItems.filter((i) => i.parentTempId === item.tempId);
  const childTypes = HIERARCHY_RULES[item.type] || [];
  const isContainer = CONTAINER_TYPES.includes(item.type);

  return (
    <>
      <div className="template-tree-node" style={{ paddingLeft: `${depth * 24 + 8}px` }}>
        {isContainer && children.length > 0 ? (
          <span
            className={`backlog-tree-chevron${expanded ? ' backlog-tree-chevron--open' : ''}`}
            onClick={() => setExpanded((v) => !v)}
          >
            <ChevronRightIcon size={14} />
          </span>
        ) : (
          <span className="backlog-tree-chevron backlog-tree-chevron--leaf" />
        )}
        <span className="template-tree-type" style={{ color: config.color }}>
          <Icon size={16} /> {config.label}
        </span>
        <input
          className="template-tree-title"
          value={item.title}
          onChange={(e) => onUpdate(item.tempId, { title: e.target.value })}
          placeholder="Title"
        />
        <input
          className="template-tree-points"
          type="number"
          min="0"
          value={item.storyPoints || ''}
          onChange={(e) => onUpdate(item.tempId, { storyPoints: e.target.value === '' ? null : Number(e.target.value) })}
          placeholder="Pts"
        />
        {childTypes.length > 0 && (
          <button className="btn-icon template-tree-add" title="Add child" onClick={() => onAddChild(item.tempId, childTypes[0])}>
            <PlusIcon size={14} />
          </button>
        )}
        <button className="btn-icon template-tree-delete" title="Delete" onClick={() => onDelete(item.tempId)}>
          <DeleteIcon size={14} />
        </button>
      </div>
      {expanded && children
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((child) => (
          <TemplateTreeNode
            key={child.tempId}
            item={child}
            allItems={allItems}
            depth={depth + 1}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onAddChild={onAddChild}
          />
        ))
      }
    </>
  );
}

export default function TemplateEditorModal({ template, onClose, onSaved, showToast, userProfile }) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [templateType] = useState(template?.templateType || 'scrum');
  const [items, setItems] = useState(template?.items || []);
  const [saving, setSaving] = useState(false);

  const rootItems = items.filter((i) => !i.parentTempId)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleAddRoot = (type) => {
    const maxOrder = items.reduce((max, i) => Math.max(max, i.order || 0), 0);
    setItems((prev) => [...prev, {
      tempId: generateTempId(),
      parentTempId: null,
      type,
      title: '',
      description: '',
      storyPoints: null,
      order: maxOrder + 1,
    }]);
  };

  const handleAddChild = (parentTempId, type) => {
    const siblings = items.filter((i) => i.parentTempId === parentTempId);
    const maxOrder = siblings.reduce((max, i) => Math.max(max, i.order || 0), 0);
    setItems((prev) => [...prev, {
      tempId: generateTempId(),
      parentTempId,
      type,
      title: '',
      description: '',
      storyPoints: null,
      order: maxOrder + 1,
    }]);
  };

  const handleUpdate = (tempId, updates) => {
    setItems((prev) => prev.map((i) => i.tempId === tempId ? { ...i, ...updates } : i));
  };

  const handleDelete = (tempId) => {
    const toRemove = new Set();
    const collect = (id) => {
      toRemove.add(id);
      items.filter((i) => i.parentTempId === id).forEach((c) => collect(c.tempId));
    };
    collect(tempId);
    setItems((prev) => prev.filter((i) => !toRemove.has(i.tempId)));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }
    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        templateType,
        items: items.filter((i) => i.title.trim()),
      };
      if (template?.id) {
        await api.updateTemplate(template.id, data);
      } else {
        await api.createTemplate({ ...data, createdBy: userProfile.uid });
      }
      showToast(template?.id ? 'Template updated' : 'Template created', 'success');
      onSaved();
    } catch (err) {
      showToast('Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="modal-footer-actions">
      <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary" onClick={handleSave} disabled={saving || !name.trim()}>
        {saving ? 'Saving...' : (template?.id ? 'Update' : 'Create')}
      </button>
    </div>
  );

  return (
    <Modal title={template?.id ? 'Edit Template' : 'New Template'} onClose={onClose} footer={footer}>
      <div className="template-editor">
        <div className="form-group">
          <label>Name</label>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" autoFocus />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea className="form-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" />
        </div>

        <div className="form-group">
          <label>Template Type</label>
          <div className="template-type-selector">
            <button className={`template-type-option${templateType === 'scrum' ? ' template-type-option--active' : ''}`} disabled>
              Scrum
            </button>
            <button className="template-type-option template-type-option--disabled" disabled title="Coming soon">
              Kanban <span className="template-type-coming-soon">Coming Soon</span>
            </button>
          </div>
        </div>

        <div className="template-tree-header">
          <label>Items</label>
          <div className="template-tree-add-roots">
            <button className="btn btn-ghost btn-sm" onClick={() => handleAddRoot('epic')}>
              <PlusIcon size={14} /> Epic
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleAddRoot('story')}>
              <PlusIcon size={14} /> Story
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => handleAddRoot('task')}>
              <PlusIcon size={14} /> Task
            </button>
          </div>
        </div>

        <div className="template-tree">
          {rootItems.length === 0 ? (
            <div className="template-tree-empty">No items. Add an Epic, Story, or Task to get started.</div>
          ) : (
            rootItems.map((item) => (
              <TemplateTreeNode
                key={item.tempId}
                item={item}
                allItems={items}
                depth={0}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onAddChild={handleAddChild}
              />
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
